import { Component, ElementRef, HostBinding, Input, OnDestroy, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { ScreenSizeService } from 'src/app/core/services/screen-size.service';
import { TenantUserPrivilegesService } from 'src/app/tenant-selector/services/tenant-user-privileges.service';

@Component({
  selector: 'app-ui-button',
  templateUrl: './ui-button.component.html',
  styleUrls: ['./ui-button.component.scss'],
  standalone: false,
})
export class UiButtonComponent implements OnDestroy {
  @ViewChild(MatTooltip) private matTooltip?: MatTooltip;

  @Input() text = 'button';
  @Input() color: 'primary' | 'warn' | 'accent' = 'primary';
  @Input() disabled?: boolean;
  @Input() buttonType: 'raised' | 'mat' | 'link' | 'fab' | 'icon-button' | 'stroked' | 'secondary' =
    'raised';
  @Input() iconVariant: 'secondary' | 'tertiary' | 'legacy' = 'tertiary';
  @Input() ariaLabel: string;
  @Input() icon: string;
  /** When true, `icon` is only rendered while the viewport is mobile. */
  @Input() iconMobileOnly = false;
  /** Shown on mobile when `icon` is empty (e.g. cancel vs back). */
  @Input() mobileFallbackIcon: string;
  @Input() iconPosition: 'left' | 'right' = 'left';
  @Input() iconColor?: string;
  @Input() actionType: 'button' | 'submit' = 'button';
  @Input() set adminPrivilege(adminPrivilege: string) {
    this.hasAdminPermission = this.tenantUserPrivileges.userHasPrivilege(adminPrivilege);
  }
  @Input() set privilege(privilege: string | string[]) {
    this.hasPermission = this.checkPermission(privilege);
  }
  @Input() privilegeStrategy: 'any' | 'all' = 'any';
  @Input() loading = false;
  @Input() tooltip: string;
  @Input() tooltipPosition: 'above' | 'below' | 'left' | 'right' = 'below';
  @Input() tooltipClass: string;

  private hasPermission = true;
  private hasAdminPermission = false;
  private readonly isMobile = toSignal(this.screenSizeService.isMobile$, {
    initialValue: this.screenSizeService.isMobileSubject.value,
  });

  get isDisabled(): boolean {
    if (this.loading) {
      return true;
    }
    // If disabled is explicitly true, always disable (form validation should override permissions)
    if (this.disabled === true) {
      return true;
    }

    return !this.hasPermission && !this.hasAdminPermission;
  }

  get displayedIcon(): string {
    if (this.icon) {
      if (this.iconMobileOnly && !this.isMobile()) {
        return '';
      }
      return this.icon;
    }
    if (this.mobileFallbackIcon && this.isMobile()) {
      return this.mobileFallbackIcon;
    }
    return '';
  }

  get buttonClasses(): string {
    if (this.buttonType === 'icon-button' && this.iconVariant === 'legacy') {
      return 'ui-button--legacy-icon';
    }
    const appearance = this.buttonAppearance;
    return `ui-button-control ui-button--${appearance}`;
  }

  private get buttonAppearance(): string {
    if (this.buttonType === 'icon-button') {
      return `icon ui-button--${this.color === 'warn' ? 'warning-outlined' : this.iconVariant}`;
    }
    if (this.buttonType === 'secondary') {
      return 'tertiary';
    }
    if (this.buttonType === 'mat' || this.buttonType === 'link') {
      return this.buttonType === 'link' ? 'text ui-button--link' : 'text';
    }
    if (this.buttonType === 'stroked') {
      return this.color === 'warn' ? 'warning-outlined' : 'secondary';
    }
    return this.color === 'warn' ? 'warning' : 'primary';
  }

  get displayableTooltip(): string {
    return this.tooltip && !this.hasAdminPermission
      ? this.tooltip
      : this.disabled !== true && !this.hasPermission && !this.hasAdminPermission
        ? 'noAccess'
        : '';
  }

  constructor(
    private elementRef: ElementRef,
    private tenantUserPrivileges: TenantUserPrivilegesService,
    private screenSizeService: ScreenSizeService
  ) {
    this.elementRef.nativeElement.addEventListener('click', this.captureClick, true);
  }

  @HostBinding('class.ui-button-disabled') get disabledClass() {
    return this.isDisabled;
  }

  @HostBinding('class.ui-button-back-action') get backActionClass() {
    return this.displayedIcon === 'chevron_left';
  }

  onClick(event: MouseEvent) {
    this.captureClick(event);
  }

  checkPermission(privilege: string | string[]): boolean {
    if (!privilege || (privilege instanceof Array && privilege.length === 0)) {
      return true;
    }
    return this.privilegeStrategy === 'any'
      ? this.tenantUserPrivileges.userHasPrivilege(privilege)
      : this.tenantUserPrivileges.userHasAllPrivileges(
          Array.isArray(privilege) ? privilege : [privilege]
        );
  }

  ngOnDestroy() {
    this.elementRef.nativeElement.removeEventListener('click', this.captureClick, true);
  }

  private captureClick = (event: MouseEvent) => {
    if (this.isDisabled) {
      event.stopPropagation();
      event.preventDefault();
      event.stopImmediatePropagation();
      // Capture-phase block prevents the mobile tap directive from receiving the click.
      this.matTooltip?.toggle();
      return false;
    }
    return true;
  };
}
